import { useState, useRef } from 'react';
import type { ReactNode } from 'react';
import Button from '@mui/material/Button';
import type { SxProps, Theme } from '@mui/material/styles';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

interface DropdownOption {
  value?: unknown;
  text?: ReactNode;
  [key: string]: unknown;
}

interface DropdownButtonProps {
  sx?: SxProps<Theme>;
  text?: ReactNode;
  options?: DropdownOption[];
  textKey?: string;
  valueKey?: string;
  noButton?: boolean;
  onChange?: (event: React.MouseEvent, value: unknown) => void;
  renderer?: (args: { setOpen: (open: boolean) => void }) => ReactNode;
  children?: ReactNode;
}

export default function DropdownButton(props: DropdownButtonProps) {
  const {
    sx,
    text,
    options = [],

    textKey,
    valueKey,

    noButton,
    onChange,

    renderer,
  } = props;

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleMenuItemClick = (event: React.MouseEvent, value: unknown) => {
    onChange?.(event, value);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (
      anchorRef.current
      && anchorRef.current.contains(event.target as Node)
    ) {
      return;
    }

    setOpen(false);
  };

  const content = renderer
    ? renderer({ setOpen })
    : (
      <MenuList autoFocusItem>
        {options.map((option) => {
          const itemValue = valueKey ? option[valueKey] : option.value;
          const itemText = textKey ? option[textKey] : option.text;

          return (
            <MenuItem
              key={String(itemValue)}
              onClick={(event) => handleMenuItemClick(event, itemValue)}
            >
              {itemText as ReactNode}
            </MenuItem>
          );
        })}
      </MenuList>
    );

  return (
    <>
      {
        !noButton && (
          <Button
            ref={anchorRef}
            sx={sx}
            onClick={handleToggle}
            endIcon={open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          >
            {text}
          </Button>
        )
      }

      <Popper
        sx={{
          // workaround:
          // the table head of the sticky table(EnhancedTable)
          // requires zIndex:1 for prevent the overlapping,
          // that will causes the Popper overlapped by the table header
          // set the Popper zIndex to 10 for workaround
          zIndex: 10,
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                {content as React.ReactElement}
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
